import {
  Alert,
  Badge,
  Button,
  cn,
  CopyButton,
  Modal,
  toast,
  useCopyToClipboard,
} from "@gotts.ai/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AnimatedBalance } from "../../components/AnimatedBalance";
import { CollapsibleSection } from "../../components/CollapsibleSection";
import { DecayCurve } from "../../components/DecayCurve";
import { PcbCard } from "../../components/PcbCard";
import { TokenIcon } from "../../components/TokenIcon";
import { useDeployment } from "../../context";
import { useTokenBalances } from "../../hooks/useTokenBalances";
import { formatTokenDisplay } from "../../lib/format";

import { DutchOrderForm } from "./DutchOrderForm";
import {
  computeCurrentOutput,
  formatDuration,
  formatRelativeTime,
  REACTOR_DESCRIPTIONS,
  REACTOR_SUBTITLES,
  STATUS_VARIANTS,
} from "./types";

import type { DutchOrder } from "./types";
import type { JSX } from "react";

// ---------------------------------------------------------------------------
// Stat SVG Icons (14x14)
// ---------------------------------------------------------------------------

function ClipboardIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
function CheckmarkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function ChartIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

const STAT_ICONS: Record<string, () => JSX.Element> = {
  "Total Orders": ClipboardIcon,
  Active: ClockIcon,
  Filled: CheckmarkIcon,
  "Fill Rate": ChartIcon,
};

// ---------------------------------------------------------------------------
// Compact Stats Bar (inline, not cards)
// ---------------------------------------------------------------------------

function StatsBar({ orders }: { orders: DutchOrder[] }) {
  const totalOrders = orders.length;
  const filledOrders = orders.filter((o) => o.status === "filled");
  const activeOrders = orders.filter((o) => o.status === "active");
  const fillRateNum =
    totalOrders > 0 ? (filledOrders.length / totalOrders) * 100 : 0;

  const fillRateColor =
    fillRateNum >= 80 ? "#34d16a" : fillRateNum >= 50 ? "#C9A84C" : "#ef4444";

  const stats: { label: string; value: React.ReactNode; color?: string }[] = [
    {
      label: "Total Orders",
      value: <AnimatedBalance value={totalOrders} integer emDash={false} />,
    },
    {
      label: "Active",
      value: (
        <AnimatedBalance value={activeOrders.length} integer emDash={false} />
      ),
      color: activeOrders.length > 0 ? "#C9A84C" : undefined,
    },
    {
      label: "Filled",
      value: (
        <AnimatedBalance value={filledOrders.length} integer emDash={false} />
      ),
      color: "#34d16a",
    },
    {
      label: "Fill Rate",
      value: (
        <AnimatedBalance
          value={fillRateNum}
          format={(n) => n.toFixed(0)}
          suffix="%"
          emDash={false}
        />
      ),
      color: fillRateColor,
    },
  ];

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
      {stats.map((s) => {
        const Icon = STAT_ICONS[s.label];
        return (
          <div key={s.label} className="flex items-center gap-1.5">
            {Icon && (
              <span className="text-white/20">
                <Icon />
              </span>
            )}
            <span className="font-mono text-[10px] uppercase tracking-wider text-white/30">
              {s.label}
            </span>
            <span
              className="font-mono text-sm font-semibold"
              style={
                s.color
                  ? { color: s.color }
                  : { color: "var(--color-text-dim)" }
              }
            >
              {s.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Countdown Timer
// ---------------------------------------------------------------------------

function CountdownTimer({
  decayEndTime,
  decayStartTime,
}: {
  decayEndTime: number;
  decayStartTime: number;
}) {
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const total = decayEndTime - decayStartTime;
  const elapsed = Math.max(0, now - decayStartTime);
  const remaining = Math.max(0, decayEndTime - now);
  const progress = total > 0 ? Math.min(elapsed / total, 1) : 1;

  const barColor =
    progress < 1 / 3 ? "#34d16a" : progress < 2 / 3 ? "#C9A84C" : "#ef4444";

  return (
    <div className="flex items-center gap-2">
      <div className="h-1 flex-1 overflow-hidden rounded-full bg-[rgba(255,255,255,0.08)]">
        <div
          className="h-full rounded-full transition-all duration-1000"
          style={{
            width: `${String(progress * 100)}%`,
            background: barColor,
          }}
        />
      </div>
      <span className="shrink-0 font-mono text-[11px] font-medium text-white/50">
        {remaining > 0 ? formatDuration(remaining) : "Decayed"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active Order Card (compact)
// ---------------------------------------------------------------------------

function ActiveOrderCard({
  order,
  onCancel,
  onFill,
}: {
  order: DutchOrder;
  onCancel: () => void;
  onFill: () => void;
}) {
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const decayDuration = order.decayEndTime - order.decayStartTime;
  const elapsed = Math.max(0, now - order.decayStartTime);
  const startAmt = parseFloat(order.startAmount);
  const endAmt = parseFloat(order.endAmount);
  const currentOutput = computeCurrentOutput(
    startAmt,
    endAmt,
    decayDuration,
    elapsed,
  );

  const [showRaw, setShowRaw] = useState(false);

  return (
    <PcbCard className="[&_.pcb-card-inner]:p-0" cut={8}>
      <div className="p-3">
        {/* Header: pair + status + actions */}
        <div className="mb-2 flex items-center gap-2">
          <TokenIcon symbol={order.inputToken} size={18} />
          <span className="font-mono text-[11px] text-white/20">
            {"\u2192"}
          </span>
          <TokenIcon symbol={order.outputToken} size={18} />
          <span className="font-mono text-xs font-semibold text-[var(--color-gold)]">
            {order.inputToken}/{order.outputToken}
          </span>
          <span className="ml-auto font-mono text-[9px] text-white/15">
            {order.id}
          </span>
          <Badge variant={STATUS_VARIANTS[order.status]}>{order.status}</Badge>
        </div>

        {/* Key metrics */}
        <div className="mb-2 grid grid-cols-1 gap-1.5 text-[11px] sm:grid-cols-3 sm:gap-2">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-wider text-white/20">
              Selling
            </div>
            <div className="mt-0.5 font-mono text-white/60">
              {formatTokenDisplay(order.inputAmount, 4).display}{" "}
              {order.inputToken}
            </div>
          </div>
          <div>
            <div className="font-mono text-[9px] uppercase tracking-wider text-white/20">
              Range
            </div>
            <div className="mt-0.5 font-mono">
              <span className="text-green-400">
                {formatTokenDisplay(order.startAmount, 4).display}
              </span>
              <span className="text-white/15"> {"\u2192"} </span>
              <span className="text-[#C9A84C]">
                {formatTokenDisplay(order.endAmount, 4).display}
              </span>
            </div>
          </div>
          <div>
            <div className="font-mono text-[9px] uppercase tracking-wider text-white/20">
              Current
            </div>
            <div className="mt-0.5 font-mono font-semibold text-white/80">
              <AnimatedBalance
                value={currentOutput}
                decimals={4}
                emDash={false}
              />
            </div>
          </div>
        </div>

        {/* Countdown */}
        <CountdownTimer
          decayStartTime={order.decayStartTime}
          decayEndTime={order.decayEndTime}
        />

        {/* Mini decay curve */}
        {decayDuration > 0 && (
          <div className="mt-2">
            <DecayCurve
              startAmount={startAmt}
              endAmount={endAmt}
              decayDuration={decayDuration}
              elapsedSeconds={elapsed}
              height={60}
              compact
              outputSymbol={order.outputToken}
            />
          </div>
        )}

        {/* Actions */}
        <div className="mt-2 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            <span className="mr-1 text-[10px]">{"\u2715"}</span> Cancel
          </Button>
          <Button variant="gold" size="sm" onClick={onFill}>
            <span className="mr-1 text-[10px]">{"\u26A1"}</span> Fill
          </Button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => {
              setShowRaw(!showRaw);
            }}
            className="text-[9px] text-white/15 transition-colors hover:text-white/30"
          >
            {showRaw ? "Hide" : "Raw"}
          </button>
        </div>

        {/* Raw data */}
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-200 ease-out",
            showRaw ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="overflow-hidden">
            <div className="mt-2">
              <pre className="overflow-x-auto rounded-sm bg-[rgba(0,0,0,0.3)] p-2 text-[9px] text-white/30">
                <code>
                  {JSON.stringify(
                    {
                      nonce: order.id,
                      deadline: order.deadline,
                      reactor: order.reactor,
                      swapper: order.maker,
                      input: {
                        token: order.inputToken,
                        amount: order.inputAmount,
                      },
                      outputs: [
                        {
                          token: order.outputToken,
                          startAmount: order.startAmount,
                          endAmount: order.endAmount,
                        },
                      ],
                      decayStartTime: order.decayStartTime,
                      decayEndTime: order.decayEndTime,
                      signature: order.signature,
                    },
                    null,
                    2,
                  )}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </PcbCard>
  );
}

// ---------------------------------------------------------------------------
// History Row (expandable)
// ---------------------------------------------------------------------------

function HistoryRow({
  order,
  onDuplicate,
}: {
  order: DutchOrder;
  onDuplicate: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { copiedKey, copy } = useCopyToClipboard();

  const decayDuration = order.decayEndTime - order.decayStartTime;
  const startAmt = parseFloat(order.startAmount);
  const endAmt = parseFloat(order.endAmount);

  let fillPositionPct: number | null = null;
  if (order.status === "filled" && order.fillOutputAmount) {
    const fillAmt = parseFloat(order.fillOutputAmount);
    const range = startAmt - endAmt;
    if (range !== 0) {
      fillPositionPct = ((startAmt - fillAmt) / range) * 100;
    }
  }

  const timeToFill =
    order.status === "filled" && order.filledAt
      ? formatDuration(Math.round((order.filledAt - order.createdAt) / 1000))
      : null;

  return (
    <>
      <tr
        className="cursor-pointer border-b border-[var(--glass-border)] transition-colors hover:bg-[rgba(201,168,76,0.025)]"
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <td className="py-2 font-mono text-[10px] text-white/20">{order.id}</td>
        <td className="py-2">
          <div className="flex items-center gap-1">
            <TokenIcon symbol={order.inputToken} size={14} />
            <span className="text-[9px] text-white/15">/</span>
            <TokenIcon symbol={order.outputToken} size={14} />
            <span className="ml-1 font-mono text-[11px] font-semibold text-[var(--color-gold)]">
              {order.inputToken}/{order.outputToken}
            </span>
          </div>
        </td>
        <td className="py-2 font-mono text-[11px] text-white/50">
          {formatTokenDisplay(order.inputAmount, 4).display} {order.inputToken}
        </td>
        <td className="py-2 font-mono text-[11px] text-white/50">
          {order.fillOutputAmount
            ? `${formatTokenDisplay(order.fillOutputAmount, 4).display} ${order.outputToken}`
            : "\u2014"}
        </td>
        <td className="py-2">
          <Badge variant={STATUS_VARIANTS[order.status]}>{order.status}</Badge>
        </td>
        <td
          className="py-2 text-right text-[10px] text-white/25"
          title={new Date(order.createdAt).toLocaleString()}
        >
          {formatRelativeTime(order.createdAt)}
        </td>
        <td className="py-2 text-right">
          <span
            className={cn(
              "inline-block text-[9px] text-white/15 transition-transform",
              expanded && "rotate-90",
            )}
          >
            {"\u25B6"}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-[var(--glass-border)]">
          <td colSpan={7} className="px-3 py-3">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs sm:grid-cols-3">
              <div>
                <span className="font-mono text-[9px] uppercase tracking-wider text-white/20">
                  Start
                </span>
                <div className="mt-0.5 font-mono text-white/40">
                  {order.startAmount} {order.outputToken}
                </div>
              </div>
              <div>
                <span className="font-mono text-[9px] uppercase tracking-wider text-white/20">
                  End
                </span>
                <div className="mt-0.5 font-mono text-white/40">
                  {order.endAmount} {order.outputToken}
                </div>
              </div>
              <div>
                <span className="font-mono text-[9px] uppercase tracking-wider text-white/20">
                  Duration
                </span>
                <div className="mt-0.5 font-mono text-white/40">
                  {formatDuration(decayDuration)}
                </div>
              </div>
              <div>
                <span className="font-mono text-[9px] uppercase tracking-wider text-white/20">
                  Reactor
                </span>
                <div className="mt-0.5 text-[11px] text-white/40">
                  {REACTOR_DESCRIPTIONS[order.reactor] ?? order.reactor}
                </div>
              </div>
              {order.fillerAddress && (
                <div>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-white/20">
                    Filler
                  </span>
                  <div className="mt-0.5 flex items-center gap-1 font-mono text-[11px] text-white/40">
                    <span>
                      {order.fillerAddress.slice(0, 6)}...
                      {order.fillerAddress.slice(-4)}
                    </span>
                    <CopyButton
                      copied={copiedKey === "filler"}
                      onClick={() => copy(order.fillerAddress ?? "", "filler")}
                      fontSize={9}
                    />
                  </div>
                </div>
              )}
              {order.fillTxHash && (
                <div>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-white/20">
                    Fill TX
                  </span>
                  <div className="mt-0.5 flex items-center gap-1 font-mono text-[11px] text-white/40">
                    <span>{order.fillTxHash.slice(0, 10)}...</span>
                    <CopyButton
                      copied={copiedKey === "tx"}
                      onClick={() => copy(order.fillTxHash ?? "", "tx")}
                      fontSize={9}
                    />
                  </div>
                </div>
              )}
              {fillPositionPct !== null && (
                <div>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-white/20">
                    Fill Position
                  </span>
                  <div className="mt-0.5 font-mono text-[11px] text-white/40">
                    {fillPositionPct.toFixed(0)}% of range
                  </div>
                </div>
              )}
              {order.status === "filled" &&
                order.fillOutputAmount &&
                startAmt !== endAmt && (
                  <div>
                    <span className="font-mono text-[9px] uppercase tracking-wider text-white/20">
                      Decay Used
                    </span>
                    <div className="mt-0.5 font-mono text-[11px] text-white/40">
                      {(() => {
                        const fillAmt = parseFloat(order.fillOutputAmount);
                        const pctDecay = (
                          ((startAmt - fillAmt) / startAmt) *
                          100
                        ).toFixed(0);
                        return `${pctDecay}% decay`;
                      })()}
                    </div>
                  </div>
                )}
              {timeToFill && (
                <div>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-white/20">
                    Time to Fill
                  </span>
                  <div
                    className={cn(
                      "mt-0.5 font-mono text-[11px]",
                      (() => {
                        if (!order.filledAt) return "text-white/40";
                        const seconds = Math.round(
                          (order.filledAt - order.createdAt) / 1000,
                        );
                        if (seconds < 30) return "text-green-400";
                        if (seconds <= 120) return "text-[#C9A84C]";
                        return "text-red-400";
                      })(),
                    )}
                  >
                    {timeToFill}
                  </div>
                </div>
              )}
            </div>

            {/* Decay curve with fill marker */}
            {order.status === "filled" && decayDuration > 0 && (
              <div className="mt-2">
                <DecayCurve
                  startAmount={startAmt}
                  endAmount={endAmt}
                  decayDuration={decayDuration}
                  fillPrice={
                    order.fillOutputAmount
                      ? parseFloat(order.fillOutputAmount)
                      : undefined
                  }
                  height={60}
                  compact
                  outputSymbol={order.outputToken}
                />
              </div>
            )}

            <div className="mt-2 border-t border-[var(--glass-border)] pt-2">
              <Button variant="outline" size="sm" onClick={onDuplicate}>
                Edit & Resubmit
              </Button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function UniswapXOrders() {
  const { deployment } = useDeployment();
  const { connectionError, refetch: refetchBalances } = useTokenBalances();

  const [orders, setOrders] = useState<DutchOrder[]>([]);
  const [prefill, setPrefill] = useState<Partial<DutchOrder> | null>(null);
  const [showReactorsModal, setShowReactorsModal] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  // HOW IT WORKS state — lifted so it persists across form re-mounts
  const [howItWorksDismissed, setHowItWorksDismissed] = useState(() => {
    try {
      return localStorage.getItem("devenv:uniswapx-how-it-works") === "hidden";
    } catch {
      return false;
    }
  });
  const handleDismissHowItWorks = useCallback(() => {
    setHowItWorksDismissed(true);
    try {
      localStorage.setItem("devenv:uniswapx-how-it-works", "hidden");
    } catch {
      // ignore
    }
  }, []);

  const reactorData = deployment?.uniswapX
    ? Object.entries(deployment.uniswapX).map(([key, addr]) => ({
        contract: REACTOR_DESCRIPTIONS[key] ?? key,
        address: String(addr),
      }))
    : [];

  const { copiedKey: reactorCopiedKey, copy: reactorCopy } =
    useCopyToClipboard();

  // Simulated filler address for dev
  const fillerAddress =
    "0x70997970C51812dc3A010C7d01b50e0d17dc79C8" as `0x${string}`;

  function handleOrderCreated(order: DutchOrder) {
    setOrders((prev) => [order, ...prev]);
    setPrefill(null);
    refetchBalances();
  }

  function handleCancelOrder(orderId: string) {
    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId ? { ...o, status: "cancelled" as const } : o,
      ),
    );
    toast.info(`Order ${orderId} cancelled`);
  }

  function handleFillOrder(orderId: string) {
    let fillMessage = `Order ${orderId} filled`;
    setOrders((prev) =>
      prev.map((o) => {
        if (o.id !== orderId) return o;
        const decayDuration = o.decayEndTime - o.decayStartTime;
        const elapsed = Math.max(
          0,
          Math.floor(Date.now() / 1000) - o.decayStartTime,
        );
        const fillAmt = computeCurrentOutput(
          parseFloat(o.startAmount),
          parseFloat(o.endAmount),
          decayDuration,
          elapsed,
        );
        fillMessage = `Order ${o.id} filled: received ${formatTokenDisplay(fillAmt.toFixed(6), 4).display} ${o.outputToken} for ${formatTokenDisplay(o.inputAmount, 4).display} ${o.inputToken}.`;
        return {
          ...o,
          status: "filled" as const,
          filledAt: Date.now(),
          fillOutputAmount: fillAmt.toFixed(6),
          fillerAddress,
          fillTxHash: `0x${Date.now().toString(16).padStart(64, "0")}`,
        };
      }),
    );
    toast.success(fillMessage);
    refetchBalances();
  }

  const handleDuplicateOrder = useCallback((order: DutchOrder) => {
    setPrefill(order);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, []);

  const activeOrders = useMemo(
    () => orders.filter((o) => o.status === "active"),
    [orders],
  );
  const historyOrders = useMemo(
    () => orders.filter((o) => o.status !== "active"),
    [orders],
  );
  const hasOrders = orders.length > 0;

  return (
    <div>
      {reactorData.length > 0 && (
        <div className="mb-4 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowReactorsModal(true);
            }}
          >
            Reactors ({reactorData.length})
          </Button>
        </div>
      )}

      {/* Reactors Modal */}
      {showReactorsModal && (
        <Modal
          open={showReactorsModal}
          onOpenChange={(open) => {
            if (!open) setShowReactorsModal(false);
          }}
          title="Deployed Reactor Contracts"
        >
          <div className="space-y-3">
            {reactorData.map((r) => {
              const reactorKey = Object.entries(REACTOR_DESCRIPTIONS).find(
                ([, v]) => v === r.contract,
              )?.[0];
              const subtitle = reactorKey
                ? REACTOR_SUBTITLES[reactorKey]
                : undefined;
              const isDefault = reactorKey === "exclusiveDutchOrderReactor";
              return (
                <div
                  key={r.address}
                  className="flex items-center justify-between rounded-sm border border-[var(--glass-border)] bg-[rgba(255,255,255,0.02)] px-3 py-2"
                >
                  <div>
                    <div className="flex items-center gap-2 text-xs font-medium text-white/60">
                      {r.contract}
                      {isDefault && <Badge variant="info">DEFAULT</Badge>}
                    </div>
                    {subtitle && (
                      <div className="mt-0.5 text-[10px] italic text-white/25">
                        {subtitle}
                      </div>
                    )}
                    <div className="mt-0.5 font-mono text-[10px] text-white/20">
                      {r.address}
                    </div>
                  </div>
                  <CopyButton
                    copied={reactorCopiedKey === r.address}
                    onClick={() => reactorCopy(r.address, r.address)}
                    fontSize={10}
                  />
                </div>
              );
            })}
          </div>
        </Modal>
      )}

      {connectionError && (
        <Alert variant="error" title="Anvil Unreachable" className="mb-4">
          Cannot connect to Anvil at 127.0.0.1:8545. Is it running?
        </Alert>
      )}

      {/* Stats bar — compact inline when orders exist */}
      {hasOrders && <StatsBar orders={orders} />}

      {/* ---- Main layout: form + orders side by side when orders exist ---- */}
      {hasOrders ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[420px_1fr]">
          {/* Left: form */}
          <div ref={formRef}>
            <DutchOrderForm
              onOrderCreated={handleOrderCreated}
              prefill={prefill}
              howItWorksDismissed={howItWorksDismissed}
              onDismissHowItWorks={handleDismissHowItWorks}
            />
          </div>

          {/* Right: active orders + history */}
          <div className="space-y-4">
            {/* Active Orders */}
            {activeOrders.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[var(--color-gold)]">
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#C9A84C]" />
                  Active (
                  <AnimatedBalance
                    value={activeOrders.length}
                    integer
                    emDash={false}
                  />
                  )
                </div>
                <div className="space-y-3">
                  {activeOrders.map((order) => (
                    <ActiveOrderCard
                      key={order.id}
                      order={order}
                      onCancel={() => {
                        handleCancelOrder(order.id);
                      }}
                      onFill={() => {
                        handleFillOrder(order.id);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {activeOrders.length === 0 && (
              <div className="py-2 text-[11px] text-white/20">
                No active orders
              </div>
            )}

            {/* Order History */}
            {historyOrders.length > 0 && (
              <CollapsibleSection
                title={
                  <span className="font-mono text-[10px] uppercase tracking-wider text-white/25">
                    History (
                    <AnimatedBalance
                      value={historyOrders.length}
                      integer
                      emDash={false}
                    />
                    )
                  </span>
                }
                defaultOpen
                className="border-none"
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--glass-border)]">
                        <th className="py-1.5 text-left font-mono text-[9px] font-normal uppercase tracking-wider text-white/15">
                          ID
                        </th>
                        <th className="py-1.5 text-left font-mono text-[9px] font-normal uppercase tracking-wider text-white/15">
                          Pair
                        </th>
                        <th className="py-1.5 text-left font-mono text-[9px] font-normal uppercase tracking-wider text-white/15">
                          Input
                        </th>
                        <th className="py-1.5 text-left font-mono text-[9px] font-normal uppercase tracking-wider text-white/15">
                          Output
                        </th>
                        <th className="py-1.5 text-left font-mono text-[9px] font-normal uppercase tracking-wider text-white/15">
                          Status
                        </th>
                        <th className="py-1.5 text-right font-mono text-[9px] font-normal uppercase tracking-wider text-white/15">
                          Time
                        </th>
                        <th className="w-4" />
                      </tr>
                    </thead>
                    <tbody>
                      {historyOrders.map((order) => (
                        <HistoryRow
                          key={order.id}
                          order={order}
                          onDuplicate={() => {
                            handleDuplicateOrder(order);
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </CollapsibleSection>
            )}
          </div>
        </div>
      ) : (
        /* ---- Hero state: centered form + empty state ---- */
        <div>
          <div ref={formRef}>
            <DutchOrderForm
              onOrderCreated={handleOrderCreated}
              prefill={prefill}
              howItWorksDismissed={howItWorksDismissed}
              onDismissHowItWorks={handleDismissHowItWorks}
            />
          </div>
          <div className="py-6 text-center">
            <div className="text-2xl text-white/10">{"\u2696"}</div>
            <p className="mt-1 text-xs text-white/20">
              Create a Dutch auction order above. Fillers compete for the best
              price.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
